'use client'

import type { Data } from '@repo/strapi-types'
import { useMemo, useRef, useState } from 'react'
import CkEditorCSRRenderer from '@/components/elementary/CkEditorRenderer/CkEditorCSRRenderer'
import { BasicImage } from '@/components/page-builder/components/BasicImage'
import { Paragraph } from '@/components/typography/Paragraph'
import { cn } from '@/lib/styles'
import { EXPANDABLE_FEATURES_LIST_TEST_IDS } from '@/lib/test-ids'
import type { ServerRenderedEntry } from '@/types/helpers'
import { getColorVariant } from '@/utils/colorMapping'

const SimpleTimedAccordion = ({
  items,
  className,
}: {
  items: ServerRenderedEntry<Data.Component<'shared.feature'>>[] | null | undefined
  className?: string
}) => {
  const [selectedStep, setSelectedStep] = useState<number>(0)

  const selectedItem = useMemo(() => {
    return items?.[selectedStep]
  }, [items, selectedStep])

  const [maxHeight, setMaxHeight] = useState<number>(0)
  const [currentMaxHeight, setCurrentMaxHeight] = useState<number>(0)
  const heightsRef = useRef<number[]>([])

  const setHeights = (el: HTMLDivElement | null, index: number) => {
    if (!el) return
    const scrollHeight = el.scrollHeight ?? 0
    heightsRef.current[index] = scrollHeight

    const newMaxHeight = Math.max(...heightsRef.current)
    if (newMaxHeight !== maxHeight) {
      setMaxHeight(newMaxHeight)
    }

    if (index === selectedStep) {
      el.style.maxHeight = `${scrollHeight}px`
      setCurrentMaxHeight(scrollHeight)
    } else {
      el.style.maxHeight = '0px'
    }
  }

  return (
    <div className={cn('flex w-full lg:gap-10 gap-4 lg:flex-row flex-col', className)}>
      <div className="relative flex flex-col lg:w-1/2">
        <ol
          className="relative flex flex-col gap-3 transition-padding duration-500 lg:duration-300 ease-in-out"
          style={{ paddingBottom: `${maxHeight - currentMaxHeight}px` }}
        >
          {items?.map((step, index) => (
            <li key={step.id} data-test-id={EXPANDABLE_FEATURES_LIST_TEST_IDS.FEATURE_TRIGGER}>
              <button
                className={cn(
                  'flex flex-col gap-4 lg:flex-row  w-full px-5 lg:py-5 py-3 rounded-md transition-colors duration-300 ease-in-out cursor-pointer',
                  index === selectedStep && 'bg-white',
                )}
                onClick={() => {
                  setSelectedStep(index)
                }}
                type="button"
              >
                <div
                  className={cn(
                    'flex shrink-0 items-center justify-center border-2 lg:border rounded-full size-7 lg:size-8 border-black lg:flex!',
                    index !== selectedStep && 'hidden',
                  )}
                >
                  <Paragraph fontSize="bodyBoldSmall">{index + 1}</Paragraph>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex gap-2 lg:gap-4 items-center">
                    <div
                      className={cn(
                        'flex shrink-0 items-center justify-center rounded-full size-7 lg:size-8 border-2 lg:border border-black lg:hidden',
                        index === selectedStep && 'hidden',
                      )}
                    >
                      <Paragraph fontSize="bodyBoldSmall">{index + 1}</Paragraph>
                    </div>

                    {step.serverProcessedHtmlTitle ? (
                      step.serverProcessedHtmlTitle
                    ) : (
                      <CkEditorCSRRenderer
                        htmlContent={step.title}
                        variant="textOnly"
                        className="w-fit min-h-7 lg:min-h-8 text-left"
                        defaultTypography="headingMedium"
                      />
                    )}
                  </div>

                  <div
                    ref={(el) => setHeights(el, index)}
                    className={cn(
                      'flex flex-col gap-2 overflow-hidden duration-500 lg:duration-300 ease-in-out h-full max-h-0',
                      index === selectedStep
                        ? 'opacity-100'
                        : 'opacity-0 pointer-events-none duration-500 lg:duration-300',
                    )}
                  >
                    {step.serverProcessedHtmlDescription ? (
                      step.serverProcessedHtmlDescription
                    ) : (
                      <CkEditorCSRRenderer
                        htmlContent={step.description}
                        variant="textOnly"
                        className="text-start"
                        defaultTypography="bodyLarge"
                      />
                    )}

                    {step?.image ? (
                      <BasicImage
                        key={step.image.id}
                        component={step.image}
                        className={cn(
                          'self-start object-contain flex rounded-md object-center w-full! h-auto! lg:hidden',
                          getColorVariant(step?.imageBackgroundColor?.color, 'bg', 'transparent'),
                        )}
                      />
                    ) : null}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ol>
      </div>

      <div
        className={cn(
          'hidden lg:grid w-1/2 rounded-md place-items-center',
          getColorVariant(selectedItem?.imageBackgroundColor?.color, 'bg', 'neutral-light-100'),
        )}
      >
        {selectedItem?.image && (
          <BasicImage
            key={selectedItem.image.id}
            component={selectedItem.image}
            className="object-contain w-full"
          />
        )}
      </div>
    </div>
  )
}

export default SimpleTimedAccordion
